// F1: the page which observes a due activity finishes it. Boundaries only advance the turn/clock.
import { isSelfStatusOrigin } from '../janus-engine/statuses/status-origin.js';
import { resolveTimedFinish } from '../janus-engine/actions/ability-use.js';
import { dueTimedActivities, timedActivityFamily, timedFinishSelection, timedFinishRangeRefusal } from '../janus-engine/ability/timed-finish.js';
import { timedFinishScopeFor, timedFinishScopeHolds, timedFinishOwnerIds, type TimedFinishScope as Scope } from '../janus-engine/ability/timed-finish-scope.js';
import { ecId, ecLean, itemField, record, upper, HandBack, type Row, type CampaignClock, type CounterspellWatchSet } from '../janus-engine/ability/context.js';
import { selectionVisibilityVerdict } from '../janus-engine/actions/cast-range-gate.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { TRIGGER_CONSENT_REASON } from '../janus-engine/ability/trigger-consent.js';
import { abilityTruthReaders, commitZoneEntryVerdict, hydrateReferencedDefinitions, timedFinishPlacedReaders } from './ability-truth.js';
import { actorEncounterFor, actorRowFor } from './movement-truth.js';
import { commitTimedEquipFinish } from './inventory-equip-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { fetchAndCacheCaster, withSettledCasterReads } from './caster-cache.js';
import { ensureActivityMembers } from './activity-members.js';
import { planWithConsent, offerFortunate } from './trigger-consent.js';
import { skipUnported } from '../janus-engine/unported.js';
import { statusItemId, statusRows } from '../janus-engine/statuses/expiry.js';
import { DISENCHANT_ABILITY, ENCHANTING_SKILL, DUST_ITEMS } from '../janus-engine/ability/timed-finish/activities.js';
import { fabricateRows } from '../janus-engine/actions/ability-preconditions.js';
import { recordingRollSource, tapedRollSource, type RecordedDraw } from '../janus-engine/actions/roll-source.js';
import { loadReadingFacts } from './book-truth.js';
import { SUMMON_LAYERS } from '../janus-engine/ability/summon.js';
import { prepareSpawnEffects } from '../shared/spawn-browser.js';
import { readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers';

type Request = { encounterId: string; scope: Scope; clock?: CampaignClock };
const installing = ((window as unknown as { __ftTimedFinishInstalling?: Set<string> }).__ftTimedFinishInstalling ??= new Set<string>());
const skipped = ((window as unknown as { __ftTimedFinishSkips?: Set<string> }).__ftTimedFinishSkips ??= new Set<string>());
// Equipment has no dice to replay. Keep its due status and retry on a later boundary,
// with one warning per status/page session and no tight retry loop across bundle copies.
const pendingEquipment = ((window as unknown as { __ftPendingTimedEquipment?: Map<string, number> }).__ftPendingTimedEquipment ??= new Map<string, number>());
const equipmentReady = (statusId: string): boolean => (pendingEquipment.get(statusId) ?? 0) <= Date.now();
function deferEquipment(statusId: string, reason: string): void {
  const first = !pendingEquipment.has(statusId);
  pendingEquipment.set(statusId, Date.now() + 15_000);
  if (!first) return;
  console.warn('[TIMED-EQUIP-PENDING]', { statusId, reason });
  window.dispatchEvent(new CustomEvent('ft:timed-equip-pending', { detail: { statusId, reason } }));
  warn('The equipment change could not finish. It is still pending and will retry automatically. Check the item’s requirements, or reload the page.');
}
const drawsByStatus = ((window as unknown as { __ftTimedFinishDraws?: Map<string, RecordedDraw[]> }).__ftTimedFinishDraws ??= new Map<string, RecordedDraw[]>());
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const scalar = (v: unknown): unknown => Array.isArray(v) ? v[0] : v;
const bucket = (e: Row): Row => record(e.encounters) ?? e;
/** Materialize identity through the page reader; instance layers, storage and computed overrides stay intact. */
function catalogRow(row: Row | null, definition: (id: string) => Row | null): Row | null {
  if (!row) return null;
  const id = upper(ftItemsCol(row, 'item_id'));
  const def = id ? definition(id) : null;
  const catalog = { ...(def && id ? { [id]: def } : {}), ...record(row.catalog) };
  const held = { ...row, catalog };
  const items = { ...record(row.items) };
  for (const key of ['item_id', 'item_name', 'item_type']) {
    const value = ftItemsCol(held, key) ?? ftItemsCol(def, key);
    if (value != null) items[key] = value;
  }
  const out: Row = { ...held, items };
  for (const key of ['inventory', 'inventory_in_container']) {
    if (Array.isArray(row[key])) out[key] = row[key].map(entry => catalogRow(record(entry), definition));
  }
  return out;
}

/** Warm only missing inventory identities, as turn prefetch does for thinned status references. */
async function prefetchInventoryIdentities(rows: readonly Row[]): Promise<void> {
  const wanted = new Set<string>();
  const visit = (row: Row): void => {
    if (record(row.inventory) && !ftItemsCol(row, 'item_type')) {
      const id = upper(ftItemsCol(row, 'item_id'));
      if (UUID.test(id)) wanted.add(id);
    }
    for (const key of ['inventory', 'inventory_in_container']) {
      if (Array.isArray(row[key])) for (const entry of row[key]) { const child = record(entry); if (child) visit(child); }
    }
  };
  rows.forEach(visit);
  for (const id of wanted) await fetchAndCacheObject(id).catch(() => null);
}
const encounterIdentity = (e: Row): string => upper(scalar(bucket(e).encounter_id ?? e.encounter_id ?? record(e.items)?.item_id));
// Some served embeds seeded scalar encounter_id[0], i.e. cache key "C". Read the row identity, never a key's first character.
function heldEncounter(id: string): Row | null {
  const direct = actorEncounterFor(id) as Row | null;
  if (direct && encounterIdentity(direct) === upper(id)) return direct;
  for (const value of window.FateTide?.encounterCache?.values() ?? []) {
    const e = record(value);
    if (e && encounterIdentity(e) === upper(id)) return e;
  }
  return null;
}
const roster = (e: Row): Row[] => (Array.isArray(e.encounter_creatures_arr) ? e.encounter_creatures_arr : []).filter((m): m is Row => !!record(m));
/** Peer truth can retire this exact activity while its definitions, birth inputs or consent load. */
function activityHeld(encounter: Row | null, actorId: string, status: Row): boolean {
  const actor = encounter ? roster(encounter).find(m => ecId(m) === actorId) ?? null : null;
  return statusRows(actor, 'target').some(s => upper(s.status_id) === upper(status.status_id)
    && upper(scalar(s.status_connected_id)) === upper(scalar(status.status_connected_id))
    && Number(scalar(s.status_deleted) ?? 0) === 0);
}
/** Whether any owner holds a timed activity at all. With none, no clock makes one due (dueTimedActivities only returns these). */
function holdsTimedActivities(rows: readonly Row[], ownerIds: readonly string[] | 'all'): boolean {
  const owners = ownerIds === 'all' ? null : new Set(ownerIds.map(upper));
  return rows.some(owner => (!owners || owners.has(ecId(owner))) && statusRows(owner, 'target').some(status => !!timedActivityFamily(status)));
}
const viewer = (): string => upper(window.FateTide?.user?.id);
function gm(e: Row): boolean {
  const b = bucket(e), uid = viewer();
  if (!UUID.test(uid)) return false;
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  const parent = upper(b.encounter_master_parent_id);
  const c = record(ft?.campaignCache?.get(parent) ?? ft?.currentCampaignArr);
  const cb = record(c?.campaigns) ?? c;
  return upper(b.encounter_created_by_id) === uid
    || (upper(cb?.campaign_id) === parent && upper(cb?.campaign_gm) === uid)
    || roster(e).some(m => record(m.permissions)?.campaign === true);
}
function mayAct(e: Row, owner: Row): boolean {
  if (gm(e)) return true;
  const controller = upper(record(owner.encounter_creatures)?.controller_encounter_creature_id);
  const c = UUID.test(controller) ? actorRowFor(encounterIdentity(e), controller) as Row | null
    ?? roster(e).find(m => ecId(m) === controller) : owner;
  return !!c && record(c.permissions)?.player === true && (upper(scalar(record(c.encounter_creatures)?.encounter_creature_created_by_id)) === viewer() || upper(ftLayerValue(c, 'permissioned')) === viewer());
}
function warn(message: unknown): void {
  if (typeof message === 'string' && message) {
    const w = window as unknown as { displayWarning?: (s: string) => void };
    w.displayWarning?.(message);
  }
}
async function refresh(ids: readonly string[]): Promise<void> {
  await Promise.all([...new Set(ids.filter(id => UUID.test(id)))].map(id =>
    withSettledCasterReads(() => fetchAndCacheCaster(id, { skipEncounterSeed: true, throwOnError: true }))));
}
async function skipFinish(encounterId: string, actorId: string, status: Row, scope: Scope, before: ReadonlyMap<string, Row>, reason: string): Promise<void> {
  const held = heldEncounter(encounterId);
  if (!held || !scopeHolds(held, scope)) return;
  const statusId = upper(status.status_id), sourceId = upper(status.status_origin_id);
  const connection = upper(scalar(status.status_connected_id)), skipKey = statusId + '|' + connection;
  drawsByStatus.delete(statusId);
  if (!activityHeld(held, actorId, status)) return;
  if (skipped.has(skipKey)) return;
  skipped.add(skipKey); // One delivery attempt per activity generation, including persistent refusals.
  const removed = new Map([[statusId, { status_id: statusId, status_item_id: statusItemId(status), target: actorId }]]);
  const heldTargets = new Set([encounterId, ...roster(held).map(ecId)]);
  const nonHeldPeers = new Map<string, string>();
  if (connection) for (const member of before.values()) {
    const buckets = record(member.statuses);
    for (const name of ['target', 'caster']) for (const peer of Array.isArray(buckets?.[name]) ? buckets[name] : []) {
      const row = record(peer);
      if (!row || Number(scalar(row.status_deleted) ?? 0) !== 0 || upper(scalar(row.status_connected_id)) !== connection) continue;
      const id = upper(row.status_id), target = upper(scalar(row.status_target_id));
      if (heldTargets.has(target)) removed.set(id, { status_id: id, status_item_id: statusItemId(row), target });
      else nonHeldPeers.set(id, target);
    }
  }
  // The existing timed-finish claim gives the skip the SAME operation identity as a supported finish.
  const residue = 'loss-triggers, shapeshift/special-status cleanup, summon-tag cleanup, expiry log and non-page-visible connected peers skipped'
    + [...nonHeldPeers].map(([id, target]) => `; connected peer ${id} targeting ${target || '(missing)'} skipped: target-not-held`).join('');
  const actor = before.get(actorId);
  const name = String(actor ? ecLean(actor, 'encounter_creature_name') ?? itemField(actor, 'item_name') ?? 'The acting creature' : 'The acting creature');
  const message = { ...skipUnported(`${reason}; ${name}; ${residue}`, { encounterId, actorId, statusId, sourceId }), roll_item_id: sourceId,
    roll_by: actorId, roll_to: actorId };
  const verdict: Parameters<typeof commitZoneEntryVerdict>[0]['verdict'] = {
    kind: 'resolved', rows: [message], ec: {}, statusesAdded: [],
    statusesRemoved: [...removed.values()],
    charges: [], playerCharactersChanged: [], inventory: [], deathTail: [], concentrationBreaks: [], statusStacks: [],
  };
  try {
    const saved = await commitZoneEntryVerdict({ encounterId, actorId, abilityId: sourceId, zoneStatusId: '', verdict, before,
      timedFinish: { statusId, connectionId: connection } });
    if (saved.outcome === 'served') return;
  } catch (error) { console.warn('[TIMED-FINISH-SKIP]', statusId, error); }
  const encounter = heldEncounter(encounterId);
  if (encounter && gm(encounter)) warn('Timed finish skip delivery could not be confirmed; this status will not be retried during this page session.');
}

/** Every campaign clock part a finite integer (A12 Codex review P2: a supplied clock with a missing part read as NaN, and a NaN compare made
 *  every unpaused activity look due). */
const clockValid = (c: CampaignClock | undefined): c is CampaignClock => !!c
  && ['year', 'month', 'day', 'hour', 'minute', 'second'].every(p => Number.isInteger((c as unknown as Record<string, unknown>)[p]));
const clockParts = ['year', 'month', 'day', 'hour', 'minute', 'second'];
function persistedClock(campaign: unknown): CampaignClock | undefined {
  const c = record(campaign), cb = record(c?.campaigns) ?? c;
  if (!cb || clockParts.some(p => scalar(cb[`campaign_time_${p}`]) == null)) return;
  const clock = Object.fromEntries(clockParts.map(p => [p, Number(scalar(cb[`campaign_time_${p}`]))])) as unknown as CampaignClock;
  return clockValid(clock) ? clock : undefined;
}
const boundaryKey = (e: Row): string => {
  const b = bucket(e);
  return [b.encounter_master_parent_id, b.active_combat_id, b.current_encounter_creature_id_turn, b.current_round, b.turn_phase, b.in_combat]
    .map(v => String(scalar(v) ?? '')).join('|');
};
// Lair/legendary phases and an END response are still the same owner's START, not a new clock sample.
const startKey = (e: Row): string => {
  const b = bucket(e);
  return [b.encounter_master_parent_id, b.active_combat_id, b.current_encounter_creature_id_turn, b.current_round, b.in_combat]
    .map(v => String(scalar(v) ?? '')).join('|');
};
// Shared by bundle copies and immediate callers: an END response on the same holder must reuse its observed START clock.
const starts = ((window as unknown as { __ftTimedFinishStarts?: Map<string, { key: string; scope: Scope | null; turnTruth: boolean }> }).__ftTimedFinishStarts ??= new Map());
function heldCampaign(e: Row): Row | null {
  const parent = upper(bucket(e).encounter_master_parent_id);
  const ft = window.FateTide as unknown as { campaignCache?: Map<string, unknown>; objectCache?: Map<string, unknown>; currentCampaignArr?: unknown } | undefined;
  for (const candidate of [ft?.campaignCache?.get(parent), ft?.objectCache?.get(parent), ft?.currentCampaignArr]) {
    const c = record(candidate), cb = record(c?.campaigns) ?? c;
    if (c && upper(scalar(cb?.campaign_id)) === parent && persistedClock(c)) return c;
  }
  return null;
}
function boundaryScope(e: Row, push = false, turnTruth = false): Scope | null {
  const scope = timedFinishScopeFor(e, push);
  if (scope?.kind !== 'turn-start') return scope;
  const eid = encounterIdentity(e), key = startKey(e), prior = starts.get(eid);
  if (prior?.key === key) return prior.scope;
  const startClock = persistedClock(heldCampaign(e));
  // An unreadable start stays unknown for this boundary; a later clock must not turn it into an END-time finish.
  const pinned = startClock && Number.isSafeInteger(scope.startRound) ? { ...scope, startClock } : null;
  starts.set(eid, { key, scope: pinned, turnTruth });
  return pinned;
}
/** The scope still holds on the encounter as this page holds it NOW (re-checked after every await, before any die — Codex review P1). */
function scopeHolds(encounter: Row | null, scope: Scope): encounter is Row {
  return timedFinishScopeHolds(encounter, scope, !!encounter && gm(encounter));
}

function finishDelay(encounter: Row): number {
  const rows = roster(encounter);
  const casting = rows.flatMap(owner => statusRows(owner, 'target')).filter(status => {
    const family = timedActivityFamily(status);
    return family === 'casting' || family === 'casting_ritual_spell';
  });
  if (!casting.length || !rows[0]) return 1500;
  const read = abilityTruthReaders(rows[0], encounter, new Set(rows.map(ecId)));
  return casting.some(status => {
    const ability = read.definition(upper(status.status_origin_id));
    return ability && SUMMON_LAYERS.some(key => read.has(ability, key));
  }) ? 0 : 1500;
}

async function finish(request: Request): Promise<void> {
  const eid = upper(request.encounterId), attempted = new Set<string>();
  const staleStatuses = new Set<string>();
  if (!UUID.test(eid)) return;
  // A14 driver: D7's full encounter install before discovery is NOT shipped — its early return when a WS frame crossed the read
  // would skip an in-combat boundary for good (the observer dedupes a repeat key). The shared timed-finish claim
  // (ability_truth_receipts) already makes a stale seed's finish a silent 409.
  if (request.scope.kind === 'turn-start') {
    if (!clockValid(request.scope.startClock)) return;
    request = { ...request, clock: request.scope.startClock };
  }
  if (!clockValid(request.clock)) request = { encounterId: request.encounterId, scope: request.scope };
  if (!request.clock) {
    const encounter = heldEncounter(eid);
    if (!encounter) return;
    // Standalone adventure/location authoring has no campaign clock. Its master parent
    // must not be requested as a campaign object when an encounter install is observed.
    const parentType = upper(scalar(bucket(encounter).encounter_master_parent_type));
    // Only a KNOWN non-campaign parent skips; '' means the held bucket lacks the field (the server always synthesizes one), so fetch as before.
    if (parentType === 'LOCATION' || parentType === 'ADVENTURE' || parentType === 'UNKNOWN') return;
    // Nothing timed on the scope's owners: no clock can make anything due, so there is no campaign to read. Every out-of-combat
    // install in each window reached here, and each read compiled the whole campaign (a party move read it 3–4×; 2026-10-26).
    // A timed row that arrives later comes with its own install, which runs this again.
    if (!holdsTimedActivities(roster(encounter), timedFinishOwnerIds(request.scope))) return;
    // End Turn's round wrap persists its clock before the answer; don't race its later websocket patch.
    const c = record(await fetchAndCacheObject(upper(bucket(encounter).encounter_master_parent_id), { fresh: true }));
    const cb = record(c?.campaigns) ?? c;
    const parts = ['year','month','day','hour','minute','second'];
    if (!cb || parts.some(p => cb[`campaign_time_${p}`] == null || !Number.isFinite(Number(scalar(cb[`campaign_time_${p}`]))))) return;
    request = { ...request, clock: Object.fromEntries(parts.map(p => [p, Number(scalar(cb[`campaign_time_${p}`]))])) as unknown as CampaignClock };
  }
  const seed = heldEncounter(eid);
  if (!scopeHolds(seed, request.scope)) return;
  const candidates = dueTimedActivities(roster(seed), request.clock!, timedFinishOwnerIds(request.scope))
    .filter(a => !skipped.has(upper(a.status.status_id)) && equipmentReady(upper(a.status.status_id)) && mayAct(seed, a.owner));
  if (!candidates.length) return;
  const participantIds = [...new Set(candidates.flatMap(a => [ecId(a.owner), upper(a.status.status_caster_id)]))];
  const readErrors = new Map<string, unknown>();
  await ensureActivityMembers(eid, participantIds, readErrors);
  for (;;) {
    const encounter = heldEncounter(eid);
    if (!scopeHolds(encounter, request.scope)) return;
    const held = roster(encounter);
    const first = held[0]; if (!first) return;
    const base = abilityTruthReaders(first, encounter, new Set(held.map(ecId)));
    const clock = request.clock ?? base.campaignClock(); if (!clock) return;
    const due = dueTimedActivities(held, clock, timedFinishOwnerIds(request.scope))
      .find(a => !attempted.has(upper(a.status.status_id)) && !skipped.has(upper(a.status.status_id)) && equipmentReady(upper(a.status.status_id)) && mayAct(encounter, a.owner));
    if (!due) return;
    const sid = upper(due.status.status_id), actorId = ecId(due.owner), casterId = upper(due.status.status_caster_id);
    const heldAbility = base.definition(upper(due.status.status_origin_id));
    const summonFinish = (due.family === 'casting' || due.family === 'casting_ritual_spell')
      && !!heldAbility && SUMMON_LAYERS.some(key => base.has(heldAbility, key));
    attempted.add(sid);
    try {
      for (const id of [actorId, casterId]) if (readErrors.has(id)) throw readErrors.get(id);
      const fresh = heldEncounter(eid);
      if (!scopeHolds(fresh, request.scope)) return;
      const all = new Map(roster(fresh).map(m => [ecId(m), m])), actor = all.get(actorId);
      if (!actor || !mayAct(fresh, actor)) continue;
      const heldRead = abilityTruthReaders(actor, fresh, new Set([...all].filter(([,m]) => !!record(record(m.computed)?.creature) && !!record(m.item_layer_types)).map(([id]) => id)));
      const read = { ...heldRead, campaignClock: () => request.clock ?? heldRead.campaignClock() };
      const live = read.statuses(actor).find(s => upper(s.status_id) === sid);
      if (!live || !read.campaignClock() || !dueTimedActivities([actor], read.campaignClock()!, [actorId]).some(a => upper(a.status.status_id) === sid && a.outcome !== 'waiting')) continue;
      if (due.family === 'donning' || due.family === 'doffing') {
        try {
          const saved = await commitTimedEquipFinish({ encounterId: eid, actorId, statusId: sid, inventoryId: upper(live.status_origin_id), donning: due.family === 'donning' });
          if (saved.ok) pendingEquipment.delete(sid);
          else if (saved.status !== 409) deferEquipment(sid, saved.error ?? `timed-finish:${due.family}:unresolved`);
        } catch (error) { deferEquipment(sid, String((error as Error)?.message ?? error)); }
      } else if (due.family === 'casting' || due.family === 'casting_ritual_spell' || due.family === 'reading' || due.family === 'training'
          || due.family === 'gathering' || due.family === 'lockpicking' || due.family === 'disenchanting') {
        const casting = due.family === 'casting' || due.family === 'casting_ritual_spell';
        const selfOrigin = isSelfStatusOrigin(upper(live.status_id), upper(live.status_origin_id));
        const loaded = selfOrigin ? null : record(await fetchAndCacheObject(upper(live.status_origin_id), { fresh: !summonFinish }).catch(() => null));
        if (!scopeHolds(heldEncounter(eid), request.scope)) return; // the boundary moved while the definition loaded
        const source = selfOrigin ? null : casting ? read.definition(upper(live.status_origin_id))
          : loaded && upper(read.inventoryField?.(loaded, 'inventory_id')) === upper(live.status_origin_id) ? loaded
            : read.resolveInventory?.(upper(live.status_origin_id)) ?? read.definition(upper(live.status_origin_id));
        await prefetchInventoryIdentities([...(source ? [source] : []), ...all.values()]);
        if (!scopeHolds(heldEncounter(eid), request.scope)) return;
        const ability = catalogRow(source, read.definition);
        if (ability && !ftItemsCol(ability, 'item_type')) {
          await skipFinish(eid, actorId, live, request.scope, all,
            `timed-finish:${due.family}:source-catalog-item-type-not-held:${String(ftItemsCol(ability, 'item_name') ?? 'activity source')}`);
          continue;
        }
        if (ability) await hydrateReferencedDefinitions(actor, fresh, [ability]);
        if (!scopeHolds(heldEncounter(eid), request.scope)) return;
        const yields = due.family === 'gathering' && ability ? fabricateRows(read.entries(ability, 'fabricate')) : [];
        const references = due.family === 'disenchanting' ? [DISENCHANT_ABILITY, ENCHANTING_SKILL, ...DUST_ITEMS]
          : yields.flatMap(y => [y.uuid, y.template]).filter(Boolean);
        for (const id of references) await fetchAndCacheObject(upper(id)).catch(() => null);
        if (!scopeHolds(heldEncounter(eid), request.scope)) return;
        // Same authority-scoped watch read as ability-truth's cast. A finish with an armed watch stays pre-draw until its full hydration is twinned.
        let watchSet: CounterspellWatchSet | null = null;
        if (due.family === 'casting' && ability && String(ftItemsCol(ability, 'item_type')) === 'spell') {
          try {
            const response = await fetch('/options/game/actions/counterspell_watch_inc.php', { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ encounter_id: eid, actor_id: actorId }), signal: AbortSignal.timeout(10000) });
            const body = await response.json() as Row;
            if (response.ok && body.ok === true && Array.isArray(body.watches)) {
              const watches = body.watches.map(entry => { const w = record(entry) ?? {}; return {
                triggerId: upper(w.trigger_id), watcherId: upper(w.watcher_id), itemId: upper(w.item_id), createdAt: String(w.created_at ?? '') }; });
              if (watches.every(w => UUID.test(w.triggerId) && UUID.test(w.watcherId) && UUID.test(w.itemId))) {
                watchSet = { watches };
              }
            }
          } catch { /* Unknown watches remain unavailable to the pre-draw resolver. */ }
          if (!scopeHolds(heldEncounter(eid), request.scope)) return;
        }
        let selected: ReturnType<typeof timedFinishSelection> | null = null;
        try { selected = casting && ability ? timedFinishSelection(live, actor, ability, read) : null; }
        catch (error) {
          // Missing selection facts skip this finish without inventing targets or drawing.
          if (!(error instanceof HandBack)) throw error;
          await skipFinish(eid, actorId, live, request.scope, all, error.reason); continue;
        }
        // Hold the saved choice and the complete summon/template source package, as an immediate cast does.
        let spawnSummons = read.spawnSummons;
        let spawnDefinition: ((id: string) => Row | null) | undefined;
        if (casting && ability && SUMMON_LAYERS.some(key => read.has(ability, key))) {
          const template = upper(readLayerListEntryUuid(read.value(ability, 'summon_monster_template')));
          const child = upper(selected?.summonCreatureId || readLayerListEntryUuid(read.value(ability, 'summon')));
          try {
            const prepared = await prepareSpawnEffects([upper(live.status_origin_id)], UUID.test(template) ? [template] : [], fresh,
              UUID.test(child) ? [child] : []);
            spawnSummons = prepared.engineSummons;
            spawnDefinition = prepared.definition;
          } catch (error) {
            await skipFinish(eid, actorId, live, request.scope, all, String(error instanceof Error ? error.message : error));
            continue;
          }
          if (!scopeHolds(heldEncounter(eid), request.scope)) return;
        }
        const area = ability && selected ? timedFinishPlacedReaders(fresh, actor, ability, selected.targetId) : read.placedAoe;
        const readingFacts = due.family === 'reading' && ability ? await loadReadingFacts(eid, actor, ability).catch(() => undefined) : undefined;
        if (!scopeHolds(heldEncounter(eid), request.scope)) return;
        // The prepared spawn package owns a cold summon choice's definition.
        // Both preconditions and the planner must resolve that same held input.
        const finishDefinition = (id: string): Row | null => read.definition(id) ?? spawnDefinition?.(id) ?? null;
        const frameRead = { ...read, spawnSummons,
          ...(due.family === 'reading' ? { timedFinishReadingFacts: () => readingFacts } : {}),
          definition: (id: string) => catalogRow(finishDefinition(id), finishDefinition),
          ...(read.resolveItem ? { resolveItem: (entry: unknown) => catalogRow(read.resolveItem!(entry)
            ?? finishDefinition(upper(readLayerListEntryUuid(entry))), finishDefinition) } : {}),
          resolveInventory: (id: string) => !casting && ability && upper(id) === upper(live.status_origin_id) ? ability : catalogRow(read.resolveInventory?.(id) ?? null, read.definition),
          counterspellWatches: (id: string) => upper(id) === actorId ? watchSet : null, ...(area ? { placedAoe: area } : {}), timedFinishTargetFailure: (caster: Row, target: Row, cast: Row): string | null | undefined => {
          if (ecId(caster) === ecId(target) || upper(read.value(cast, 'range')) === SELF) return null;
          const range = timedFinishRangeRefusal(caster, target, cast, read, [...all.values()]);
          if (range === undefined) return undefined;
          if (range) return range;
          if (read.visibility?.(caster, target) == null) return undefined;
          const sight = selectionVisibilityVerdict({ caster, ability: cast, targets: [{ id: ecId(target), row: target }] }, { ...read, visibility: (c, t) => read.visibility?.(c, t) ?? null });
          if (sight.kind === 'refuse') return sight.message;
          if (sight.drop.length) return 'You cannot target into an obscured area.';
          return null;
        } };
        let drawn = 0;
        const verdict = await planWithConsent(eid, actorId, consent => {
          if (!activityHeld(heldEncounter(eid), actorId, live))
            return { kind: 'handback' as const, reason: 'timed-finish:activity-retired' };
          const readers = { ...frameRead, triggerConsent: consent };
          const members = new Map([...all].map(([id, member]) => [id, catalogRow(member, read.definition)!]));
          const plan = (roll: Parameters<typeof resolveTimedFinish>[4]) => resolveTimedFinish({ encounterId: eid, statusId: sid }, fresh, members, readers, roll);
          // Engine dry runs tolerate residual branches that are unavailable at commit. Exercise the WHOLE finish on the same
          // extreme/alternating faces before a browser die, so those branches hand back here instead of throwing after drawing.
          for (const pattern of ['max', 'min', 'alt1-hi', 'alt1-lo', 'alt2-hi', 'alt2-lo', 'alt3-hi', 'alt3-lo']) {
            let n = 0;
            const high = (): boolean => pattern === 'max' || (pattern !== 'min' && (Math.floor(n / Number(pattern[3])) % 2 === 0) === pattern.endsWith('-hi'));
            const proof = plan({ d: die => { const face = high() ? die : 1; n++; return face; }, fraction: () => { const face = high() ? 0.999999 : 0; n++; return face; } });
            if (proof.kind === 'handback') return proof;
          }
          if (!scopeHolds(heldEncounter(eid), request.scope)) return { kind: 'handback' as const, reason: 'timed-finish:scope-superseded' };
          const random = browserRollSource();
          const frozen = drawsByStatus.get(sid);
          if (frozen) return plan(tapedRollSource(frozen, { d: () => { throw new HandBack('timed-finish:frozen-dice-shape-changed'); },
            fraction: () => { throw new HandBack('timed-finish:frozen-dice-shape-changed'); } }));
          const source = recordingRollSource({ d: die => { drawn++; return random.d(die); }, fraction: () => { drawn++; return random.fraction(); } });
          try { return plan(source); }
          finally { if (source.draws.length) drawsByStatus.set(sid, [...source.draws]); }
        }, v => v.kind === 'handback' && v.reason.includes(TRIGGER_CONSENT_REASON));
        if (!scopeHolds(heldEncounter(eid), request.scope)) return;
        if (!activityHeld(heldEncounter(eid), actorId, live)) { drawsByStatus.delete(sid); continue; }
        if (verdict.kind === 'handback') {
          // Even an unexpected post-draw hand-back retires the activity with no effects or reroll.
          await skipFinish(eid, actorId, live, request.scope, all,
            drawn ? `timed-finish:unsupported-after-draw:${verdict.reason}` : verdict.reason);
        }
        else if (verdict.outcome !== 'waiting') {
          const { outcome, message, ...claim } = verdict;
          const saved = await commitZoneEntryVerdict({ encounterId: eid, actorId, abilityId: upper(live.status_origin_id), zoneStatusId: '', verdict: claim,
            before: all, timedFinish: { statusId: sid, connectionId: upper(scalar(live.status_connected_id)) } });
          if (saved.outcome === 'served') {
            drawsByStatus.delete(sid);
            if (outcome === 'refused') warn(message);
            if (verdict.fortunateOffers?.length) offerFortunate(eid, actorId, verdict.fortunateOffers);
          }
          else if (saved.outcome === 'stale') {
            if (!staleStatuses.has(sid)) { staleStatuses.add(sid); await refresh([actorId, casterId]); attempted.delete(sid); }
            else await skipFinish(eid, actorId, live, request.scope, all, `timed-finish:${due.family}:stale-after-refresh`);
          } else if (saved.outcome === 'refused') {
            await skipFinish(eid, actorId, live, request.scope, all,
              `timed-finish:${due.family}:${saved.reason ?? 'commit-refused'}`);
          } else throw new Error(`Timed finish delivery could not be confirmed (${saved.reason ?? saved.outcome}); the due status remains pending.`);
        }
      } else await skipFinish(eid, actorId, live, request.scope, all, `timed-finish:${due.family}:not-ported`);
    } catch (error) {
      // A failed read, delivery or stale recovery stays attempted for this drain; later rows still run.
      if (due.family === 'donning' || due.family === 'doffing') deferEquipment(sid, String((error as Error)?.message ?? error));
      else {
        console.warn('[TIMED-FINISH]', sid, error);
        warn(String((error as Error)?.message ?? error));
      }
    }
  }
}
// Shared across bundle copies; immediate callers queue behind an in-flight observer instead of losing the new boundary.
const queues = ((window as unknown as { __ftTimedFinishQueues?: Map<string, Promise<void>> }).__ftTimedFinishQueues ??= new Map());
export function finishDueTimedActivities(request: Request): Promise<void> {
  const seed = actorEncounterFor(upper(request.encounterId)) as Row | null;
  const eid = seed ? encounterIdentity(seed) : upper(request.encounterId);
  if (!UUID.test(eid)) return Promise.resolve();
  request = { ...request, encounterId: eid };
  if (request.scope.kind === 'turn-start') {
    let scope = request.scope;
    if (!scope.startClock && !scope.captured) {
      const e = heldEncounter(eid);
      if (!scopeHolds(e, scope)) return Promise.resolve();
      const pinned = boundaryScope(e);
      if (pinned?.kind !== 'turn-start' || upper(pinned.holderId) !== upper(scope.holderId)) return Promise.resolve();
      scope = pinned;
    }
    if (!clockValid(scope.startClock) || scope.combatId === undefined || !Number.isSafeInteger(scope.startRound)) return Promise.resolve();
    request = { ...request, scope: { ...scope, startClock: { ...scope.startClock } } };
  }
  const prior = queues.get(eid) ?? Promise.resolve();
  // AB1's cross-lane ask (lane A12): the campaign page and its FT3D scene iframe are TWO windows with their own sockets, so every
  // WebSocket-observed boundary reaches this twice per tab. The Web Locks API is shared by same-origin windows: one window finishes the
  // encounter's due activities at a time; installed truth and the finish receipt retire competing work.
  const locks = (navigator as Navigator & { locks?: { request: (name: string, cb: () => Promise<void>) => Promise<void> } }).locks;
  const next = prior.catch(() => undefined).then(() => locks ? locks.request('ft-timed-finish:' + eid, () => finish(request)) : finish(request));
  queues.set(eid, next);
  void next.finally(() => { if (queues.get(eid) === next) queues.delete(eid); }).catch(() => undefined);
  return next;
}
if (!(window as unknown as { __ftTimedFinishMounted?: boolean }).__ftTimedFinishMounted) {
  (window as unknown as { __ftTimedFinishMounted?: boolean }).__ftTimedFinishMounted = true;
  const observed = new Map<string, string>(), timers = new Map<string, number>();
  // Coordinator Round 30 P2 #4 (lane A13; was A12 pick (c)): the observer debounce captures the SCOPE (the turn holder) when it is SCHEDULED,
  // from the frame it observed — a turn that changes inside the 1.5 s window no longer carries the previous holder's due finish a round late.
  // Capture its clock too: a late GM drain may only finish activities due at that START, not ones due at the holder's END.
  const run = (eid: string, push = false, clock?: CampaignClock, captured?: Scope | null): void => {
    const e = heldEncounter(eid); if (!e || !UUID.test(eid)) return;
    const scope = captured ?? boundaryScope(e, push); if (!scope) return;
    void finishDueTimedActivities({ encounterId: eid, ...(clock ? { clock } : {}), scope })
      .catch(error => console.warn('[TIMED-FINISH]', error));
  };
  const observe = (event: Event): void => {
    const detail = record((event as CustomEvent).detail), installed = record(detail?.encounterArr);
    const seed = installed ?? actorEncounterFor(upper(detail?.encounterId)) as Row | null;
    const eid = seed ? encounterIdentity(seed) : upper(detail?.encounterId);
    const e = installed ?? heldEncounter(eid); if (!e || !UUID.test(eid)) return;
    if (installing.has(eid)) return; // this drain's own read must not schedule another OOC drain
    // active_combat_id in the key (A13 closing review P2): a NEW combat with the same holder / round / phase is a new boundary
    const b = bucket(e), key = boundaryKey(e);
    // Out-of-combat installs can bring newly due rows without changing a turn scalar.
    if (Number(scalar(b.in_combat)) === 1 && observed.get(eid) === key) return;
    observed.set(eid, key);
    // one pending debounce PER HOLDER: a newer turn's frame no longer cancels the previous holder's pending finish (Round 30 P2 #4)
    const scope = boundaryScope(e, false, detail?.source === 'turn-truth'); if (!scope) return;
    const tk = scope?.kind === 'turn-start' ? eid + '|' + scope.holderId : eid;
    clearTimeout(timers.get(tk));
    timers.set(tk, window.setTimeout(() => { timers.delete(tk); run(eid, false, undefined, scope?.kind === 'turn-start' ? { ...scope, captured: true } : scope); }, finishDelay(e)));
  };
  for (const name of ['ft:encounter-turn-state', 'ft:encounter-state-received', 'ft:encounter-installed']) window.addEventListener(name, observe);
  const turnClockOperations = new Map<string, Extract<Scope, { kind: 'turn-start' }>>();
  // A provisional wrap waits for commit, but its START clock remains the persisted sample observed at that boundary.
  // Neither a command's increment nor a later saved clock may move this due-test cutoff.
  window.addEventListener('ft:turn-truth-applied', event => {
    const d = record((event as CustomEvent).detail), command = record(d?.command), frame = record(command?.frame), next = record(frame?.encounter);
    if (!command || command.correction === true || !next || Number(frame?.clock_seconds) !== 6) return;
    const oid = upper(command.operation_id);
    if (!UUID.test(oid)) return;
    if (command.committed === true) {
      for (const [operation, captured] of turnClockOperations) if (operation.startsWith(oid + '|')) {
        captured.pending = false;
        run(upper(command.encounter_id), false, undefined, { ...captured, captured: true });
      }
      return;
    }
    const eid = upper(command.encounter_id), e = heldEncounter(eid), held = e ? starts.get(eid) : null, scope = held?.scope;
    if (!e || !held?.turnTruth || scope?.kind !== 'turn-start' || !clockValid(scope.startClock) || held.key !== startKey(e)) return;
    // A refused operation's render carries its old command after rollback. It is not the held START boundary.
    if (upper(next.current_encounter_creature_id_turn) !== scope.holderId || Number(next.current_round) !== Number(scalar(bucket(e).current_round))) return;
    const operation = [oid, scope.combatId, held.key].join('|');
    if (turnClockOperations.has(operation)) return;
    turnClockOperations.set(operation, scope);
    scope.pending = true; // a provisional START cannot finish anything until its operation commits
  });
  // Saved clock installs follow checkpoint answers and campaign_update, independently of the
  // display's derived running seconds. A due permissioned row queues the same observer debounce.
  const observeClock = (campaignId: unknown, clock: CampaignClock | undefined): void => {
    if (!clockValid(clock)) return;
    for (const value of window.FateTide?.encounterCache?.values() ?? []) {
      const e = record(value), eid = e ? encounterIdentity(e) : '';
      if (!e || !UUID.test(eid) || Number(scalar(bucket(e).in_combat)) === 1 || upper(bucket(e).encounter_master_parent_id) !== upper(campaignId)) continue;
      if (!dueTimedActivities(roster(e), clock, 'all').some(a => mayAct(e, a.owner))) continue;
      clearTimeout(timers.get(eid));
      timers.set(eid, window.setTimeout(() => { timers.delete(eid); run(eid, false, clock); }, finishDelay(e)));
    }
  };
  window.addEventListener('ft:campaign-clock-installed', event => {
    const d = record((event as CustomEvent).detail);
    observeClock(d?.campaignId, (record(d?.cols) ?? undefined) as unknown as CampaignClock | undefined);
  });
  // Scene embeds can hold encounters without mounting the campaign clock runtime. Consume the
  // installed campaign_update there too; both events share the same per-encounter debounce.
  window.addEventListener('ft:campaign-state-received', event => {
    const d = record((event as CustomEvent).detail), campaign = record(d?.campaignArr);
    const cb = record(campaign?.campaigns) ?? campaign;
    if (!cb) return;
    const parts = ['year', 'month', 'day', 'hour', 'minute', 'second'];
    if (parts.some(p => cb[`campaign_time_${p}`] == null)) return;
    observeClock(d?.campaignId ?? cb.campaign_id, Object.fromEntries(parts.map(p => [p, Number(scalar(cb[`campaign_time_${p}`]))])) as unknown as CampaignClock);
  });
  window.addEventListener('ft:timed-finish-now', event => {
    const d = record((event as CustomEvent).detail), eid = upper(d?.encounterId), e = heldEncounter(eid), held = starts.get(eid);
    // The sender emits this after accepting its turn commit. Peers promote through the committed command above.
    if (d?.push !== true && e && held?.key === startKey(e) && held.scope?.kind === 'turn-start') held.scope.pending = false;
    clearTimeout(timers.get(eid)); run(eid, d?.push === true, (record(d?.clock) ?? undefined) as unknown as CampaignClock | undefined);
  });
  // Ability responses are not encounter boundaries. Their resource/inventory/status changes arrive
  // through the normal row deltas; fetching and reinstalling the entire encounter here reset only
  // the caster's FT3D scene after every potion throw (the GM never receives this local event).
  // Lair/legendary abilities can advance the turn: use the installed scalar frame when it arrived
  // before the response, otherwise the turn-state observer above finishes when that frame arrives.
  // Explicit End Turn / Push Time requests retain their fresh-boundary recovery below.
  window.addEventListener('ft:legacy-action-accepted', event => {
    const d = record((event as CustomEvent).detail), data = record(d?.data), path = String(d?.post ?? '');
    if (!/end_turn_inc\.php|use_ability|push_time_inc\.php/.test(path)) return;
    const eid = upper(data?.encounter_id); if (UUID.test(eid)) {
      if (/use_ability/.test(path)) {
        const e = actorEncounterFor(eid) as Row | null;
        if (e && typeof d?.timedTurnBefore === 'string') {
          const b = bucket(e), after = [b.current_encounter_creature_id_turn, b.current_round, b.turn_phase].join('|');
          if (d.timedTurnBefore !== after) {
            clearTimeout(timers.get(eid));
            timers.delete(eid);
            run(eid);
          }
        }
        return;
      }
      void fetchAndCacheObject(eid, { fresh: true }).then(async value => {
        if (!value || !record((value as unknown as Row).encounters)) return;
        await handleEncounterUpdate({ type: 'encounter_update', encounter_arr: value } as never);
        const e = value as unknown as Row, push = /push_time_inc\.php/.test(path);
        const b = bucket(e), after = [b.current_encounter_creature_id_turn, b.current_round, b.turn_phase].join('|');
        if (!push && d?.timedTurnBefore === after) return;
        const c = push ? record(await fetchAndCacheObject(upper(bucket(e).encounter_master_parent_id), { fresh: true })) : null;
        const cb = record(c?.campaigns) ?? c;
        const clock = cb ? Object.fromEntries(['year','month','day','hour','minute','second'].map(p => [p, Number(scalar(cb[`campaign_time_${p}`]))])) as unknown as CampaignClock : undefined;
        run(eid, push, clock);
      }).catch(() => undefined);
    }
  });
  for (const value of window.FateTide?.encounterCache?.values() ?? []) {
    const e = record(value); if (e) observe(new CustomEvent('load', { detail: { encounterId: encounterIdentity(e), encounterArr: e } }));
  }
}
Object.assign(window, { ftFinishDueTimedActivities: finishDueTimedActivities });
